import { API_URL } from "../constants";
import { getAPI, postPagedSearchAbsolute } from "./frameworksApi";
import { shortGroupName } from "../utils/formatters";
import {
  onSessionReset,
  useAuthStore,
  type AuthUser,
  type UserOrgItem,
  type UserGroupItem,
} from "../stores/authStore";

export interface UserProfileDetails {
  organizations: UserOrgItem[];
  groups: UserGroupItem[];
}

function resolveModuleAndAction(user: AuthUser | null): { module: string; action: string } {
  const perms = user?.permissions || useAuthStore.getState().user?.permissions || [];

  const parsed = perms
    .map((p: any) => {
      if (typeof p === "string") {
        const [module, action] = p.split(":");
        return { module, action: action || "view" };
      }
      return { module: p?.module, action: p?.action || "view" };
    })
    .filter((p: { module?: string }) => Boolean(p.module));

  // Prioritize organization:view, or any module with view action, or first available permission
  const orgView = parsed.find((p: any) => p.module === "organization" && p.action === "view");
  if (orgView) return orgView;

  const anyView = parsed.find((p: any) => p.action === "view");
  if (anyView) return anyView;

  if (parsed.length > 0) return parsed[0];

  return { module: "organization", action: "view" };
}

interface UserRoleScope {
  id: string;
  roleName: string;
  displayName: string;
  scopeOrganization: {
    id: string;
    name: string;
    shortName: string;
  } | null;
  isActive: boolean;
}

interface UserRecordSearch {
  id: string;
  email: string;
  username?: string;
  displayName: string;
  organizations?: Array<{ id: string; name: string; shortName?: string }>;
  platformRoles?: UserRoleScope[];
}

interface MappingForProvenance {
  mappingId: string;
  groupName: string;
  groupDn: string;
  orgId: string;
  orgName: string;
}

interface RoleWithMappings {
  id: string;
  name: string;
  groupMappings: MappingForProvenance[];
}

interface OrgUnitItem {
  id: string;
  name: string;
  shortName?: string;
  orgType?: string;
}

const CACHE_KEY_PREFIX = "user_profile_details_";
let inFlightProfilePromise: Promise<UserProfileDetails> | null = null;

export function clearUserProfileCache(userId?: string): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    if (userId) {
      sessionStorage.removeItem(`${CACHE_KEY_PREFIX}${userId}`);
    } else {
      const keysToRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(CACHE_KEY_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((k) => sessionStorage.removeItem(k));
    }
  } catch {
    // ignore
  }
}

onSessionReset(() => {
  clearUserProfileCache();
});

export function getCachedUserProfileDetails(userId?: string): UserProfileDetails | null {
  if (!userId) return null;
  try {
    const raw = sessionStorage.getItem(`${CACHE_KEY_PREFIX}${userId}`);
    if (raw) {
      return JSON.parse(raw);
    }
  } catch {
    // ignore parse error
  }
  return null;
}

export async function fetchUserProfileDetails(
  user: AuthUser | null,
): Promise<UserProfileDetails> {
  if (!user) {
    return { organizations: [], groups: [] };
  }

  // Check cache first to avoid redundant API calls
  const cached = getCachedUserProfileDetails(user.userId);
  if (cached) {
    return cached;
  }

  // Deduplicate concurrent in-flight requests
  if (inFlightProfilePromise) {
    return inFlightProfilePromise;
  }

  inFlightProfilePromise = (async () => {
    try {
      const orgMap = new Map<string, UserOrgItem>();
      const groupMap = new Map<string, UserGroupItem>();

      // 1. Initial seeds from user object in authStore
      if (user.orgName && user.orgName.trim()) {
        orgMap.set(user.orgName.trim().toLowerCase(), {
          id: user.orgId || undefined,
          name: user.orgName.trim(),
        });
      }

      if (Array.isArray(user.organizations)) {
        for (const org of user.organizations) {
          if (typeof org === "string" && org.trim()) {
            orgMap.set(org.trim().toLowerCase(), { name: org.trim() });
          } else if (org && typeof org === "object" && org.name) {
            orgMap.set(org.name.trim().toLowerCase(), {
              id: org.id,
              name: org.name.trim(),
              shortName: org.shortName,
            });
          }
        }
      }

      if (Array.isArray(user.groups)) {
        for (const g of user.groups) {
          const rawName = typeof g === "string" ? g : g?.name;
          const dn = typeof g === "object" ? g.dn : undefined;
          if (rawName && rawName.trim()) {
            const cleanName = shortGroupName(rawName.trim());
            groupMap.set(cleanName.toLowerCase(), {
              name: cleanName,
              dn: dn || rawName.trim(),
            });
          }
        }
      }

      if (Array.isArray(user.adGroups)) {
        for (const g of user.adGroups) {
          const rawName = typeof g === "string" ? g : g?.name;
          const dn = typeof g === "object" ? g.dn : undefined;
          if (rawName && rawName.trim()) {
            const cleanName = shortGroupName(rawName.trim());
            groupMap.set(cleanName.toLowerCase(), {
              name: cleanName,
              dn: dn || rawName.trim(),
            });
          }
        }
      }

      // 2. Fetch accessible organizations and groups
      const { module: dynModule, action: dynAction } = resolveModuleAndAction(user);
      const accessibleOrgsPromise = getAPI(
        `${API_URL.ACCESSIBLE_ORGS}?module=${encodeURIComponent(dynModule)}&action=${encodeURIComponent(dynAction)}&org_type=ORGANIZATION&org_type=GROUP`,
      ).catch(() => null);

      // 3. Fetch user record from users list to find mapped organizations and roles
      const userSearchPromise = (async () => {
        try {
          const keyword = user.username || user.email;
          if (!keyword) return null;
          return await postPagedSearchAbsolute<UserRecordSearch>(
            API_URL.ADMIN_USERS,
            0,
            10,
            {
              keyword,
              sortByColumn: "",
              sortingOrder: "",
              filters: {},
            },
          );
        } catch {
          return null;
        }
      })();

      // 4. Fetch platform roles with mappings to derive mapped AD groups from user roles
      const mappingsPromise = getAPI(API_URL.PLATFORM_ROLES_WITH_MAPPINGS).catch(() => null);

      const [accessibleRes, userSearchRes, mappingsRes] = await Promise.all([
        accessibleOrgsPromise,
        userSearchPromise,
        mappingsPromise,
      ]);

      // Process accessible organizations and groups
      const orgUnits: OrgUnitItem[] =
        accessibleRes?.data?.organizations || accessibleRes?.data || [];
      if (Array.isArray(orgUnits)) {
        for (const unit of orgUnits) {
          if (unit?.name && unit.name.trim()) {
            const type = (unit.orgType || "").toUpperCase();
            if (type === "GROUP") {
              const cleanName = shortGroupName(unit.name.trim());
              if (!groupMap.has(cleanName.toLowerCase())) {
                groupMap.set(cleanName.toLowerCase(), {
                  name: cleanName,
                  dn: unit.name.trim(),
                });
              }
            } else {
              const key = unit.name.trim().toLowerCase();
              if (!orgMap.has(key)) {
                orgMap.set(key, {
                  id: unit.id,
                  name: unit.name.trim(),
                  shortName: unit.shortName,
                });
              }
            }
          }
        }
      }

      // Process matched user record
      const searchItems: UserRecordSearch[] = userSearchRes?.items ?? [];
      const matchedUser = searchItems.find(
        (u) =>
          (user.username &&
            (u.username?.toLowerCase() === user.username.toLowerCase() ||
              u.email?.toLowerCase() === user.username.toLowerCase())) ||
          (user.email && u.email?.toLowerCase() === user.email.toLowerCase()) ||
          (user.userId && u.id === user.userId),
      );

      if (matchedUser) {
        if (Array.isArray(matchedUser.organizations)) {
          for (const org of matchedUser.organizations) {
            if (org?.name && org.name.trim()) {
              const key = org.name.trim().toLowerCase();
              if (!orgMap.has(key)) {
                orgMap.set(key, {
                  id: org.id,
                  name: org.name.trim(),
                  shortName: org.shortName,
                });
              }
            }
          }
        }

        // Process role to AD group mappings
        const allRoleMappings: RoleWithMappings[] = mappingsRes?.data ?? [];
        if (matchedUser.platformRoles && allRoleMappings.length > 0) {
          for (const roleGrant of matchedUser.platformRoles) {
            const roleMapping = allRoleMappings.find(
              (r) => r.name === roleGrant.roleName,
            );
            if (roleMapping?.groupMappings) {
              const orgId = roleGrant.scopeOrganization?.id ?? "";
              const relevantMappings = orgId
                ? roleMapping.groupMappings.filter((m) => m.orgId === orgId)
                : roleMapping.groupMappings;

              for (const m of relevantMappings) {
                if (m.groupName && m.groupName.trim()) {
                  const cleanName = shortGroupName(m.groupName.trim());
                  if (!groupMap.has(cleanName.toLowerCase())) {
                    groupMap.set(cleanName.toLowerCase(), {
                      name: cleanName,
                      dn: m.groupDn || m.groupName.trim(),
                    });
                  }
                }
              }
            }
          }
        }
      }

      // Fallback for organization if still empty
      if (orgMap.size === 0 && user.orgName) {
        orgMap.set(user.orgName.toLowerCase(), {
          id: user.orgId || undefined,
          name: user.orgName,
        });
      }

      const result: UserProfileDetails = {
        organizations: Array.from(orgMap.values()),
        groups: Array.from(groupMap.values()),
      };

      // Cache in sessionStorage for fast recovery on page refresh
      try {
        if (user.userId) {
          sessionStorage.setItem(
            `${CACHE_KEY_PREFIX}${user.userId}`,
            JSON.stringify(result),
          );
        }
      } catch {
        // ignore storage error
      }

      return result;
    } finally {
      inFlightProfilePromise = null;
    }
  })();

  return inFlightProfilePromise;
}

